import React from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// @mui icons
import { Delete, Edit } from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";

// Argon Dashboard 2 MUI example components
import ProfileInfoCard from "themeComponents/Cards/InfoCards/ProfileInfoCard";
import Footer from "themeComponents/Footer";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import ProfilesList from "themeComponents/Lists/ProfilesList";

// Overview page components
import Header from "layouts/profile/components/Header";
import PlatformSettings from "layouts/profile/components/PlatformSettings";

// Data
import profilesListData from "layouts/profile/data/profilesListData";

// Images
import { useEffect, useState } from "react";

// Data
import { useTheme } from "@mui/material";
import FormModal from "components/formModel";
import Loader from "components/loader";
import { convertDateToDDMMYYYY } from "configs/functions";
import useAuth from "hooks/useAuth";
import useCompany from "hooks/useCompany";
import useEmployee from "hooks/useEmployee";
import useIndustry from "hooks/useIndustry";
import { Author, Function } from "layouts/tables/data/authorsTableData";
import Badge from "themeComponents/Badge";
import * as Yup from "yup";
import ProfileTable from "./components/profileTable";
import { appLogo } from "configs/constants";
import { COMPCHECK_URL } from "configs/api";

const bgImage =
  "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/profile-layout-header.jpg";

interface EmployeeData {
  email: string;
  name: string;
  position: string;
  branchID: string;
}

function Overview(): React.JSX.Element {
  const theme = useTheme();
  const [employeeData, setEmployeeData] = useState<EmployeeData>({
    email: "",
    name: "",
    position: "",
    branchID: "",
  });

  const { getAllIndustries } = useIndustry();
  const {
    addCompany,
    getAllCompanies,
    companyFieldsAdmin,
    companyProfileTableColumns,
    addCompanyLoading,
    isCompanyUpdate,
    showAddCompany,
    setShowAddCompany,
    allCompanies
  } = useCompany();
  const {
    employeeFields,
    companyEmployeeColumns,
    deleteEmployee,
    updateEmployee,
    addEmployee,
    getCompanyEmployees,
    employees,
    addEmployeeLoading,
    showAddEmployee,
    setShowAddEmployee,
    isEmployeeUpdate,
    setIsEmployeeUpdate,
    isEmployeeDeleting,
    setIsEmployeeDeleting
  } = useEmployee();
  const { fetchUser, user, isAdmin, isCompany, isManager, isSupervisor } = useAuth();

  const companyTableRows = isAdmin
    ? allCompanies?.map((item) => ({
      company: (
        <Author
          image={item.imageURL ? `${process.env.REACT_APP_BASE_URL}${item.imageURL}` : appLogo}
          name={item.companyName}
          email={item.email}
        />
      ),
      function: <Function job={item.role} org="Organization" />,
      director: (
        <Typography variant="caption" color="secondary" fontWeight="medium">
          {item.name}
        </Typography>
      ),
      status: (
        <Badge
          variant="gradient"
          badgeContent={item.status ? "Active" : "In Active"}
          color={item.status ? "success" : "secondary"}
          size="xs"
          container
        />
      ),
      employees: (
        <Typography variant="caption" color="secondary" fontWeight="medium">
          {item.employees}
        </Typography>
      ),
      created: (
        <Typography variant="caption" color="secondary" fontWeight="medium">
          {convertDateToDDMMYYYY(item.createdAt)}
        </Typography>
      ),
      action: (
        <>
          <Edit
            onClick={() => { }}
            sx={{ cursor: "pointer", color: theme.palette.success.main }}
          />
          <Delete sx={{ cursor: "pointer", color: theme.palette.error.main, ml: 1 }} />
        </>
      ),
    }))
    : [];

  const companyTeamRows =
    isCompany || isManager
      ? employees?.map((item) => ({
        employee: (
          <Author
            image={item.imageURL ? `${COMPCHECK_URL}/api/${item.imageURL}` : appLogo}
            name={item.name}
            email={item.email}
          />
        ),
        position: <Function job={item.position} org={item.role} />,
        site: (
          <Typography variant="caption" color="secondary" fontWeight="medium">
            {item.branchID?.name}
          </Typography>
        ),
        status: (
          <Badge
            variant="gradient"
            badgeContent={item.status ? "Active" : "In Active"}
            color={item.status ? "success" : "secondary"}
            size="xs"
            container
          />
        ),
        created: (
          <Typography variant="caption" color="secondary" fontWeight="medium">
            {convertDateToDDMMYYYY(item.createdAt)}
          </Typography>
        ),
        action: (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Edit
              onClick={() => {
                setIsEmployeeUpdate(true);
                setEmployeeData({
                  email: item.email,
                  name: item.name,
                  position: item.position,
                  branchID: item.branchID?._id || "",
                });
                setShowAddEmployee(true);
              }}
              sx={{ cursor: "pointer", color: theme.palette.success.main }}
            />
            <Loader
              loading={isEmployeeDeleting === item._id}
              color={theme.palette.error.main}
              size={"small"}
              center
              style={{ marginLeft: "8px" }}
            >
              <Delete
                onClick={() => deleteEmployee(item._id)}
                sx={{ cursor: "pointer", color: theme.palette.error.main, marginLeft: "8px" }}
              />
            </Loader>
          </Box>
        ),
      }))
      : [];

  const branchesColumns = [
    { name: "name", align: "left" as const },
    { name: "location", align: "left" as const },
    { name: "managers", align: "left" as const },
    { name: "created", align: "left" as const },
    { name: "action", align: "center" as const },
  ];

  const companyBranchesRows = isCompany
    ? employees?.map((item) => ({
      name: (
        <Author
          image={item.imageURL ? `${COMPCHECK_URL}/api/${item.imageURL}` : appLogo}
          name={item.name}
          email={""}
        />
      ),
      location: (
        <Typography variant="caption" color="secondary" fontWeight="medium">
          {item.location}
        </Typography>
      ),
      managers: (
        <Typography variant="caption" color="secondary" fontWeight="medium">
          {item.managers}
        </Typography>
      ),
      created: (
        <Typography variant="caption" color="secondary" fontWeight="medium">
          {convertDateToDDMMYYYY(item.createdAt)}
        </Typography>
      ),
      action: (
        <>
          <Edit
            onClick={() => {
              setIsEmployeeUpdate(true);
              setEmployeeData({
                email: item.email,
                name: item.name,
                position: item.position,
                branchID: item.branchID?._id || "",
              });
              setShowAddEmployee(true);
            }}
            sx={{ cursor: "pointer", color: theme.palette.success.main }}
          />
          <Delete sx={{ cursor: "pointer", color: theme.palette.error.main, ml: 1 }} />
        </>
      ),
    }))
    : [];

  useEffect(() => {
    fetchUser();
    getAllIndustries();
    if (isAdmin) getAllCompanies({ page: 1, limit: 5 });
    if (isCompany && user?._id) {
      getCompanyEmployees({ companyID: user._id, page: 1, limit: 10 });
    }
  }, []);

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <DashboardLayout
      sx={{
        backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }: any) =>
          `${linearGradient(
            rgba(gradients?.info?.main, 0.6),
            rgba(gradients.info.state, 0.6)
          )}, url(${bgImage})`,
        backgroundPositionY: "50%",
      }}
    >
      {/* {showAddEmployee && (
        <FormModal
          show={showAddEmployee}
          data={employeeData}
          setData={setEmployeeData}
          validationSchema={Yup.object({
            email: Yup.string().email("Invalid email").required("Email is required"),
            name: Yup.string().required("Name is required"),
          })}
          title={isEmployeeUpdate ? "Update Employee" : "Add Employee"}
          onAddSubmit={(data) => addEmployee(data)}
          onUpdateSubmit={(data) => updateEmployee(data)}
          fields={employeeFields}
          onClose={() => setShowAddEmployee(false)}
          loading={addEmployeeLoading}
          buttonText={"Save"}
          isUpdate={isEmployeeUpdate}
        />
      )}
      {showAddCompany && (
        <FormModal
          show={showAddCompany}
          data={employeeData}
          setData={setEmployeeData}
          validationSchema={Yup.object({
            email: Yup.string().email("Invalid email").required("Email is required"),
            firstname: Yup.string().required("First name is required"),
            lastname: Yup.string().required("Last name is required"),
            telephone1: Yup.string().required("Phone number is required"),
            companyName: Yup.string().required("Company Name is required"),
          })}
          title={isCompanyUpdate ? "Update Company" : "Add Company"}
          onAddSubmit={(data) => {
            addCompany(data);
          }}
          onUpdateSubmit={(data) => { }}
          fields={companyFieldsAdmin}
          onClose={() => setShowAddCompany(false)}
          loading={addCompanyLoading}
          buttonText={"Save"}
          isUpdate={isCompanyUpdate}
        />
      )} */}
      <Header
        name={user.name}
        role={user.position ? user.position : user.role}
        imageURL={`${COMPCHECK_URL}/api/${user.imageURL}`}
      />
      <Box mt={5} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} xl={4}>
            <PlatformSettings />
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <ProfileInfoCard
              title="profile information"
              description={user?.about || "No about added yet."}
              info={{
                ...(user.email ? { email: user?.email } : {}),
                ...(user.name ? { fullName: user?.name } : {}),
                ...(user.companyName ? { companyName: user?.companyName } : {}),
                ...(user.employees ? { employees: user?.employees } : {}),
                ...(user.branchID ? { branch: user?.branchID } : {}),
                ...(user.adminID ? { assignedAdmin: user?.adminID } : {}),
                ...(user.subscriptionID ? { subscription: user?.subscriptionID } : {}),
                ...(user.firstname ? { firstName: user?.firstname } : {}),
                ...(user.lastname ? { lastName: user?.lastname } : {}),
                ...(user.telephone1 ? { mobile: user?.telephone1 } : {}),
                ...(user.address ? { address: user?.address } : {}),
                ...(user.role ? { role: user?.role } : {}),
              }}
              {...({ social: [] } as any)}
              action={{ route: "", tooltip: "Edit Profile" }}
            />
          </Grid>
          {/* <Grid item xs={12} xl={4}>
            <ProfilesList title="conversations" profiles={profilesListData} />
          </Grid> */}
        </Grid>
      </Box>
      {/* {isAdmin && (
        <ProfileTable
          title={"Registered Companies"}
          columns={companyProfileTableColumns}
          rows={companyTableRows}
          onAddClick={() => setShowAddCompany(true)}
          addTitle={"Add Company"}
        />
      )}
      {(isCompany || isManager || isSupervisor) && (
        <ProfileTable
          title={"Team"}
          columns={companyEmployeeColumns}
          rows={companyTeamRows}
          onAddClick={() => setShowAddEmployee(true)}
          addTitle={"Add Employee"}
        />
      )}
      {isCompany && (
        <ProfileTable
          title={"Branches"}
          columns={branchesColumns}
          rows={companyBranchesRows}
          onAddClick={() => setShowAddEmployee(true)}
          addTitle={"Add Branch"}
        />
      )} */}
      <Footer />
    </DashboardLayout>
  );
}

export default Overview;

